import type {
  Page,
  Settings,
  Condition,
  ConditionPost,
  Pagination,
  ConditionType,
  TableOfContents,
} from 'types';
import type { ApolloError } from '@apollo/client';
import type {
  ConditionStudyTypesQuery,
  ConditionStudyTypesQueryVariables,
  CountConditionStudiesByTypeQuery,
  CountConditionStudiesByTypeQueryVariables,
  Research_Studies_Bool_Exp,
  ConditionStudiesQuery,
  ConditionStudiesQueryVariables,
} from 'graphql/research/types-and-hooks';
import type { QueryError, QueryProps } from '../api';
import type {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from 'next';
import type { GetResultsAggregateCount } from './studies';
import {
  refetchConditionStudyTypesQuery as fetchConditionStudyTypesQuery,
  refetchCountConditionStudiesByTypeQuery as fetchCountConditionStudiesByTypeQuery,
  refetchConditionStudiesQuery as fetchConditionStudiesQuery,
} from 'graphql/research/types-and-hooks';
import camelcaseKeys from 'camelcase-keys';
import { request } from 'lib/rest';
import { initializeWpApollo, queryResearch } from '../apollo';
import { getSettings } from './settings';
import { getPageData } from './pages';
import { getResultsAggregateCount } from './studies';
import { extractH2Data } from 'lib/utils/extractH2Data';

export type AllConditionsResult = {
  conditions?: Condition[];
};

export async function getAllConditions(locale?: string): Promise<Condition[]> {
  const { error, data } = await request<Condition[]>(
    '/medical_conditions?status=publish&_fields=slug,title,link&per_page=1000&order=asc&orderby=title',
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );

  if (error) {
    throw error;
  }

  return camelcaseKeys(data.items, { deep: true });
}

export type StudiesTypeResult = {
  types?: ConditionStudyTypesQuery['research_studies'];
  error?: ApolloError;
};

export async function getStudiesTypes({
  client,
  conditionId,
}: {
  client: QueryProps['client'];
  conditionId: number;
}): Promise<StudiesTypeResult> {
  const { error: studiesTypesError, ...studiesTypes } = await queryResearch<
    ConditionStudyTypesQuery,
    ConditionStudyTypesQueryVariables
  >(
    client,
    fetchConditionStudyTypesQuery({
      conditionId,
    }),
  );

  return {
    error: studiesTypesError,
    types: studiesTypes.data.research_studies,
  };
}

export type ConditionStudiesByTypeCount = {
  count?: CountConditionStudiesByTypeQuery['research_studies_aggregate'];
  error?: ApolloError;
};

export async function countConditionStudiesByType({
  client,
  conditionId,
  type,
}: {
  client: QueryProps['client'];
  conditionId: number;
  type: string;
}): Promise<ConditionStudiesByTypeCount> {
  const {
    error: countConditionStudiesByTypeError,
    ...countConditionStudiesByType
  } = await queryResearch<
    CountConditionStudiesByTypeQuery,
    CountConditionStudiesByTypeQueryVariables
  >(
    client,
    fetchCountConditionStudiesByTypeQuery({
      conditionId,
      type,
    }),
  );

  return {
    error: countConditionStudiesByTypeError,
    count: countConditionStudiesByType.data.research_studies_aggregate,
  };
}

export type ConditionStudiesResult = {
  error?: ApolloError;
  resultsDetails?: GetResultsAggregateCount;
  studies?: ConditionStudiesQuery;
  typesDetails?: { type: string; count: number }[];
};

export async function getConditionStudies({
  client,
  conditionId,
}: {
  client: QueryProps['client'];
  conditionId: number;
}): Promise<ConditionStudiesResult> {
  if (conditionId) {
    const conditionFilter = {
      conditions: {
        medical_condition_id: { _eq: conditionId },
      },
    } as Research_Studies_Bool_Exp;

    const [studiesData, results, studiesTypeData] = await Promise.all([
      queryResearch<ConditionStudiesQuery, ConditionStudiesQueryVariables>(
        client,
        fetchConditionStudiesQuery({
          conditionId,
        }),
      ),
      getResultsAggregateCount({
        client,
        filters: conditionFilter,
      }),
      getStudiesTypes({
        client,
        conditionId,
      }),
    ]);

    const { error: errorStudies, data: studies } = studiesData;
    if (errorStudies) {
      return { error: errorStudies };
    }

    if ((results as QueryError)?.error) {
      return { error: (results as QueryError)?.error };
    }

    const { error: errorStudiesTypes, ...studiesTypes } = studiesTypeData;
    if (errorStudiesTypes) {
      return { error: errorStudiesTypes };
    }

    const studiesByTypeDetailsPromises = studiesTypes.types.map(
      async ({ typeofstudy }) => {
        const { error: errorTypesCount, ...typesCount } =
          await countConditionStudiesByType({
            client,
            conditionId,
            type: typeofstudy,
          });
        if (errorTypesCount) {
          throw errorTypesCount;
        }
        return {
          type: typeofstudy,
          count: typesCount.count.aggregate.count,
        };
      },
    );
    const studiesByTypeDetails = await Promise.all(
      studiesByTypeDetailsPromises,
    );

    return {
      studies,
      resultsDetails: results as GetResultsAggregateCount,
      typesDetails: studiesByTypeDetails,
    };
  }

  return {
    studies: null,
  };
}

export type MedicalConditionsPageProps = {
  page: Page;
  settings: Settings;
  conditions: Condition[];
  isPreview: boolean;
};

export async function getMedicalConditionsPage({
  locale,
  preview,
}: GetServerSidePropsContext): Promise<
  GetStaticPropsResult<MedicalConditionsPageProps>
> {
  const slug = locale === 'en' ? '/medical-conditions' : '/condiciones-medicas';

  try {
    const [pageData, settings, conditions] = await Promise.all([
      getPageData({ locale, slug, preview }),
      getSettings(locale),
      getAllConditions(locale),
    ]);

    if (!pageData || !settings || !conditions) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        page: pageData,
        settings: {
          ...settings,
          pageTranslations: pageData.translations,
        },
        conditions,
        isPreview: !!preview,
      },
    };
  } catch (error) {
    console.log(
      `error getting medical conditions page:`,
      JSON.stringify(error),
    );

    if (error.status === 400) {
      return {
        notFound: true,
      };
    }

    throw error;
  }
}

export type ConditionsByLetterResult = {
  conditions?: Condition[];
  letter: string;
  pagination: Pagination;
};

export async function getConditionsByLetter({
  locale,
  letter = 'a',
  page = 1,
  perPage = 500, // max results set on wordpress rest api
}: {
  locale: string;
  letter: string;
  page?: number;
  perPage?: number;
}): Promise<ConditionsByLetterResult> {
  const { error, data } = await request<Condition[]>(
    `/medical_conditions?starts_with=${letter}&order=asc&orderby=title&_fields=id,title,link,translations,language,type&page=${page}&per_page=${perPage}`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );

  if (error) {
    throw error;
  }

  return {
    conditions: data.items,
    letter,
    pagination: {
      total: data.totalItems,
      limit: perPage,
      offset: (page - 1) * perPage,
    },
  };
}

export type ResearchConditionsPageProps = {
  page: Page;
  settings: Settings;
  conditionsType: ConditionTypesResult;
  conditionsByType: ConditionsByTypeResult;
  isPreview: boolean;
};

export async function getResearchConditionsPage({
  query,
  locale,
  preview,
}: GetServerSidePropsContext): Promise<
  GetServerSidePropsResult<ResearchConditionsPageProps>
> {
  const conditionType = (query.condition_type as string)
    ? (query.condition_type as string)
    : locale == 'en'
    ? 'brain-nervous-system'
    : 'cancer-es';
  const page = query.page && Number(query.page) !== 0 ? Number(query.page) : 1;
  const slug = locale == 'en' ? '/research' : '/investigacion';

  try {
    const [pageData, conditionsByType, conditionsType, settings] =
      await Promise.all([
        getPageData({ locale, slug, preview }),
        getConditionsByType({ locale, conditionType, page, perPage: 10 }),
        getConditionTypes(locale),
        getSettings(locale),
      ]);

    if (!pageData || !settings || !conditionsByType || !conditionsType) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        page: pageData,
        settings: {
          ...settings,
          pageTranslations: pageData.translations,
        },
        conditionsByType,
        conditionsType,
        isPreview: !!preview,
      },
    };
  } catch (error) {
    if (
      error.message ==
      'The page number requested is larger than the number of pages available.'
    ) {
      return {
        redirect: {
          permanent: true,
          destination: `${slug}?page=1&condition_type=${conditionType}`,
        },
      };
    }

    console.log(`error getting research page:`, JSON.stringify(error));

    if (error.status === 400) {
      return {
        notFound: true,
      };
    }

    throw error;
  }
}

export type ConditionTypesResult = {
  conditionTypes?: ConditionType[];
};

export async function getConditionTypes(
  locale?: string,
): Promise<ConditionTypesResult> {
  const { error, data } = await request<ConditionType[]>(
    `/medical_conditions_types?_fields=id,name,slug,acf,taxonomy&per_page=100`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );

  if (error) {
    throw error;
  }
  return {
    conditionTypes: camelcaseKeys(data.items, { deep: true }),
  };
}

export type ConditionsByTypeResult = {
  conditions?: Condition[];
  conditionType: string;
  pagination: Pagination;
};

export async function getConditionsByType({
  locale,
  conditionType,
  page = 1,
  perPage = 500, // max results set on wordpress rest api
}: {
  locale: string;
  conditionType: string;
  page?: number;
  perPage?: number;
}): Promise<ConditionsByTypeResult> {
  const { error, data } = await request<Condition[]>(
    `/medical_conditions?condition_type=${conditionType}&order=asc&orderby=title&_fields=id,title,link,translations,language,type&page=${page}&per_page=${perPage}`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );
  if (error) {
    throw error;
  }

  return {
    conditions: data.items,
    conditionType,
    pagination: {
      total: data.totalItems,
      limit: perPage,
      offset: (page - 1) * perPage,
    },
  };
}

async function getSingleConditionData({
  locale,
  slug,
  preview,
}: {
  locale: string;
  slug: string;
  preview: boolean;
}): Promise<ConditionPost> {
  const { error, data } = await request<ConditionPost>(
    `/medical_conditions/?path=/${slug}&_fields=id,title,content,acf,seo,translations,language,type${
      preview ? '&status=draft' : ''
    }`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );

  if (error) {
    throw error;
  }

  return camelcaseKeys(data.items[0], { deep: true });
}

export type SingleConditionProps = {
  post: ConditionPost;
  tableOfContents: TableOfContents[];
  conditionStudies: Omit<ConditionStudiesResult, 'error'>;
  settings: Settings;
  conditionsByLetter: ConditionsByLetterResult;
  isPreview: boolean;
};

export async function getSingleCondition({
  locale,
  params,
  preview,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<SingleConditionProps>> {
  try {
    const condition = await getSingleConditionData({
      locale,
      slug: params.slug as string,
      preview,
    });

    if (!condition) {
      return {
        notFound: true,
      };
    }

    const tableOfContents = extractH2Data(condition?.content);

    const [conditionsByLetter, settings] = await Promise.all([
      getConditionsByLetter({ locale, letter: condition.title[0] }),
      getSettings(locale),
    ]);

    if (!settings || !conditionsByLetter) {
      return {
        notFound: true,
      };
    }

    const client = initializeWpApollo();
    const { error: studiesListError, ...studies } = await getConditionStudies({
      client,
      conditionId: parseInt(condition.acf.leafwellScrappingId),
    });

    if (studiesListError) {
      throw studiesListError;
    }

    if (studies) {
      tableOfContents.push({
        label: condition?.acf?.researchOverviewTitle ?? 'Research Overview',
        id: 'research-overview',
      });

      tableOfContents.push({
        label: condition?.acf?.studiesTitle ?? 'Relevant Studies',
        id: 'relevant-studies',
      });
    }

    return {
      props: {
        post: condition,
        tableOfContents,
        conditionStudies: studies,
        settings: {
          ...settings,
          pageTranslations: condition.translations,
        },
        conditionsByLetter,
        isPreview: !!preview,
      },
    };
  } catch (error) {
    console.log(`Error getting condition ${params?.slug}:`, error);

    if (error.status === 400) {
      return {
        notFound: true,
      };
    }

    throw error;
  }
}
